import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { CitysprintHelpers } from '../../../helpers';
import { findCheapestParcelServiceCardForVehicle } from '../../../helpers/mapCitysprintQuoteToParcelSections';
import {
  buildInitialQuoteState,
  buildScheduleKey,
  buildSelectionFromCard,
  findQuickQuoteMatch,
  quoteSelectionActions,
  quoteSelectionReducer,
} from '../redux/quoteSelectionSlice';
import { useCitysprintParcelQuotes } from './useCitysprintParcelQuotes';

// eslint-disable-next-line max-lines-per-function -- small hook
export function useCitysprintQuoteSelection({
  quickQuotePrefill,
  collectionDate,
  timeHour,
  timeMinute,
  collectionPostcode,
  deliveryPostcode,
  bookingSectionEnabled,
  scheduleComplete,
  collectionScheduleInPast,
  snackbar,
  onQuickQuoteConsumed,
}) {
  const [state, dispatch] = useReducer(quoteSelectionReducer, undefined, () =>
    buildInitialQuoteState({ quickQuotePrefill })
  );

  const snackbarRef = useRef(snackbar);
  snackbarRef.current = snackbar;
  const onQuickQuoteConsumedRef = useRef(onQuickQuoteConsumed);
  onQuickQuoteConsumedRef.current = onQuickQuoteConsumed;

  const quickQuotePrefillRef = useRef(quickQuotePrefill);

  const quotesFetchReady =
    bookingSectionEnabled && scheduleComplete && !collectionScheduleInPast;

  const {
    quoteLayout,
    loading: quotesLoading,
    refetchForce: refetchQuotesForce,
  } = useCitysprintParcelQuotes({
    autoFetch: false,
    enabled: quotesFetchReady,
    collectionPostcode,
    deliveryPostcode,
    collectionDate,
    timeHour,
    timeMinute,
  });

  const showVehicleQuoteSection =
    bookingSectionEnabled &&
    state.requested &&
    scheduleComplete &&
    !collectionScheduleInPast;

  useEffect(() => {
    dispatch(
      quoteSelectionActions.scheduleKeyChanged({
        key: buildScheduleKey(collectionDate, timeHour, timeMinute),
      })
    );
  }, [collectionDate, timeHour, timeMinute]);

  const fireAndReportFetch = useCallback(async () => {
    const result = await refetchQuotesForce();
    if (result?.ok) {
      dispatch(
        quoteSelectionActions.fetchSucceeded({
          validityText: CitysprintHelpers.formatQuoteValidityLineFromNow(),
        })
      );
    } else if (result?.errorMessage) {
      snackbarRef.current?.showError({ message: result.errorMessage });
    }
  }, [refetchQuotesForce]);

  useEffect(() => {
    if (
      !state.quickQuoteAutoMatchPending ||
      state.quickQuoteAutoFetchFired ||
      !quotesFetchReady
    ) {
      return;
    }
    dispatch(quoteSelectionActions.quickQuoteAutoFetchFired());
    onQuickQuoteConsumedRef.current?.();
    void fireAndReportFetch();
  }, [
    state.quickQuoteAutoMatchPending,
    state.quickQuoteAutoFetchFired,
    quotesFetchReady,
    fireAndReportFetch,
  ]);

  useEffect(() => {
    if (
      !showVehicleQuoteSection ||
      quotesLoading ||
      !quoteLayout?.vehicleCards?.length
    ) {
      return;
    }
    dispatch(quoteSelectionActions.layoutReconciled({ layout: quoteLayout }));
    if (
      state.quickQuoteAutoMatchPending &&
      !state.selection &&
      state.selectionSource !== 'user'
    ) {
      const prefill = quickQuotePrefillRef.current;
      const match = findQuickQuoteMatch(
        quoteLayout,
        prefill?.prefetchedVehicleKey,
        prefill?.selectedNetworkCode
      );
      if (match) {
        dispatch(
          quoteSelectionActions.quickQuoteMatched(buildSelectionFromCard(match))
        );
      } else {
        dispatch(quoteSelectionActions.quickQuoteMatchAttempted());
      }
    }
  }, [
    showVehicleQuoteSection,
    quotesLoading,
    quoteLayout,
    state.quickQuoteAutoMatchPending,
    state.selection,
    state.selectionSource,
  ]);

  const prevShowSectionRef = useRef(showVehicleQuoteSection);
  useEffect(() => {
    if (prevShowSectionRef.current && !showVehicleQuoteSection) {
      dispatch(quoteSelectionActions.sectionHidden());
    }
    prevShowSectionRef.current = showVehicleQuoteSection;
  }, [showVehicleQuoteSection]);

  const handleGetQuote = useCallback(async () => {
    dispatch(quoteSelectionActions.userRequestedQuote());
    await fireAndReportFetch();
  }, [fireAndReportFetch]);

  const handleSelectParcelVehicle = useCallback(
    vehicleIndex => {
      const isDeselecting = state.selectedVehicleIndex === vehicleIndex;
      const cheapestServiceCard = isDeselecting
        ? null
        : findCheapestParcelServiceCardForVehicle(quoteLayout, vehicleIndex);
      dispatch(
        quoteSelectionActions.userPickedVehicle({
          vehicleIndex,
          cheapestServiceCard,
        })
      );
    },
    [quoteLayout, state.selectedVehicleIndex]
  );

  const handleSelectQuoteOption = useCallback(card => {
    if (!card) {
      return;
    }
    dispatch(
      quoteSelectionActions.userPickedOption(buildSelectionFromCard(card))
    );
  }, []);

  const reset = useCallback(() => {
    dispatch(quoteSelectionActions.addressReset());
  }, []);

  const selectedOptionKey = useMemo(
    () => state.selection?.selectionKey ?? '',
    [state.selection]
  );

  const selectedVatAmount = useMemo(() => {
    if (!state.selection) {
      return null;
    }
    const ex = parseFloat(state.selection.prices.excl);
    const inc = parseFloat(state.selection.prices.incl);
    if (Number.isNaN(ex) || Number.isNaN(inc)) {
      return null;
    }
    return inc - ex;
  }, [state.selection]);

  return {
    selectedQuoteOption: state.selection,
    selectedVehicleIndex: state.selectedVehicleIndex,
    quoteValidityText: state.validityText,
    showVehicleQuoteSection,
    quotesFetchReady,
    quotesLoading,
    quoteLayout,
    selectedOptionKey,
    selectedVatAmount,
    handleGetQuote,
    handleSelectParcelVehicle,
    handleSelectQuoteOption,
    reset,
  };
}
